import Phaser from "phaser";
import { dispatchFlightState, FlightModel } from "./flightModel";

const WORLD_WIDTH = 480;
const WORLD_HEIGHT = 720;
const PIPE_WIDTH = 74;
const PIPE_GAP = 178;
const PIPE_DISTANCE = 252;
const PIPE_SPEED = -190;
const MIN_PIPE_GAP = 146;
const MIN_PIPE_DISTANCE = 222;
const MAX_PIPE_DISTANCE = 286;
const FASTEST_PIPE_SPEED = -305;
const GRAVITY = 1250;
const FLAP_VELOCITY = -390;
const GROUND_HEIGHT = 86;

type PipePair = {
  top: Phaser.Physics.Arcade.Image;
  bottom: Phaser.Physics.Arcade.Image;
  scored: boolean;
};

export class SnappyScene extends Phaser.Scene {
  private model = new FlightModel();
  private bird!: Phaser.Physics.Arcade.Sprite;
  private pipes!: Phaser.Physics.Arcade.StaticGroup;
  private ground!: Phaser.Physics.Arcade.Image;
  private clouds!: Phaser.GameObjects.Group;
  private pairs: PipePair[] = [];
  private flapKey?: Phaser.Input.Keyboard.Key;
  private pauseKey?: Phaser.Input.Keyboard.Key;
  private muted = false;

  constructor() {
    super("snappy");
  }

  create() {
    this.createTextures();
    this.createWorld();
    this.createInput();
    this.resetToReady();

    window.addEventListener("snappybird:start", this.handleExternalStart);
    window.addEventListener("snappybird:toggle-mute", this.handleExternalMute);
    window.addEventListener("snappybird:submitted", this.handleExternalSubmitted);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      window.removeEventListener("snappybird:start", this.handleExternalStart);
      window.removeEventListener("snappybird:toggle-mute", this.handleExternalMute);
      window.removeEventListener("snappybird:submitted", this.handleExternalSubmitted);
    });
  }

  update(_time: number, delta: number) {
    if (this.model.status !== "running") {
      this.driftReadyBird(delta);
      return;
    }

    this.wrapClouds(delta);
    this.movePipes(delta);
    this.recyclePipes();
    this.checkScoring();

    if (this.bird.y < -20 || this.bird.y > WORLD_HEIGHT - GROUND_HEIGHT + 4) {
      this.gameOver();
    }
  }

  private handleExternalStart = () => {
    if (this.model.status === "running") {
      this.flap();
      return;
    }

    this.startRun();
  };

  private handleExternalMute = () => {
    this.muted = !this.muted;
  };

  private handleExternalSubmitted = () => {
    this.model.markSubmitted();
    this.publish();
  };

  private createWorld() {
    this.add.rectangle(WORLD_WIDTH / 2, WORLD_HEIGHT / 2, WORLD_WIDTH, WORLD_HEIGHT, 0x7bd8f7);

    this.clouds = this.add.group();
    for (let index = 0; index < 7; index += 1) {
      const cloud = this.add.image(Phaser.Math.Between(20, WORLD_WIDTH - 20), Phaser.Math.Between(55, 365), "cloud");
      cloud.setAlpha(Phaser.Math.FloatBetween(0.42, 0.82));
      cloud.setScale(Phaser.Math.FloatBetween(0.58, 1.2));
      this.clouds.add(cloud);
    }

    this.add.rectangle(WORLD_WIDTH / 2, WORLD_HEIGHT - 44, WORLD_WIDTH, 88, 0x8fd653);
    this.add.rectangle(WORLD_WIDTH / 2, WORLD_HEIGHT - GROUND_HEIGHT, WORLD_WIDTH, 10, 0xf3d276);
    this.ground = this.physics.add.staticImage(WORLD_WIDTH / 2, WORLD_HEIGHT - GROUND_HEIGHT / 2, "ground");
    this.ground.setVisible(false);
    this.ground.refreshBody();

    this.pipes = this.physics.add.staticGroup();
    this.spawnInitialPipes();

    this.bird = this.physics.add.sprite(132, WORLD_HEIGHT / 2, "bird-0");
    this.bird.setCollideWorldBounds(false);
    this.bird.setDepth(5);
    this.bird.setCircle(18, 8, 8);
    this.bird.setGravityY(GRAVITY);

    this.physics.add.collider(this.bird, this.pipes, () => this.gameOver());
    this.physics.add.collider(this.bird, this.ground, () => this.gameOver());
  }

  private createInput() {
    this.flapKey = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    this.pauseKey = this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.UP);
    this.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.W);

    this.input.on("pointerdown", () => {
      if (this.model.status === "ready" || this.model.status === "gameover") {
        this.startRun();
      } else if (this.model.status === "running") {
        this.flap();
      }
    });

    this.input.keyboard?.on("keydown-SPACE", () => this.handleAction());
    this.input.keyboard?.on("keydown-UP", () => this.handleAction());
    this.input.keyboard?.on("keydown-W", () => this.handleAction());
    this.input.keyboard?.on("keydown-ESC", () => this.togglePause());
  }

  private handleAction() {
    if (this.model.status === "ready" || this.model.status === "gameover") {
      this.startRun();
      return;
    }

    if (this.model.status === "running") {
      this.flap();
    }
  }

  private togglePause() {
    if (this.model.status === "running") {
      this.model.pause();
      this.physics.pause();
      this.publish();
    } else if (this.model.status === "paused") {
      this.model.resume();
      this.physics.resume();
      this.publish();
    }
  }

  private startRun() {
    this.model.start();
    this.physics.resume();
    this.resetPipes();
    this.bird.setPosition(132, WORLD_HEIGHT / 2);
    this.bird.setVelocity(0, FLAP_VELOCITY);
    this.bird.setAngle(-14);
    this.bird.setTexture("bird-1");
    this.publish();
    this.playTone(520, 0.045, "triangle");
  }

  private resetToReady() {
    this.model.ready();
    this.physics.pause();
    this.bird.setPosition(132, WORLD_HEIGHT / 2);
    this.bird.setVelocity(0, 0);
    this.publish();
  }

  private flap() {
    this.bird.setVelocityY(FLAP_VELOCITY);
    this.bird.setAngle(-18);
    this.bird.setTexture("bird-1");
    this.tweens.add({
      targets: this.bird,
      angle: 9,
      duration: 360,
      ease: "Sine.easeIn"
    });
    this.playTone(620, 0.035, "sine");
  }

  private gameOver() {
    if (this.model.status !== "running") {
      return;
    }

    this.model.end();
    this.cameras.main.shake(160, 0.008);
    this.bird.setTexture("bird-2");
    this.bird.setVelocityY(180);
    this.playTone(150, 0.12, "sawtooth");
    this.publish();
  }

  private spawnInitialPipes() {
    this.pairs = [];
    for (let index = 0; index < 4; index += 1) {
      this.pairs.push(this.createPipePair(WORLD_WIDTH + 170 + index * PIPE_DISTANCE));
    }
  }

  private resetPipes() {
    this.pairs.forEach((pair, index) => {
      this.positionPipePair(pair, WORLD_WIDTH + 180 + index * PIPE_DISTANCE);
      pair.scored = false;
    });
  }

  private createPipePair(x: number): PipePair {
    const top = this.pipes.create(x, 0, "pipe") as Phaser.Physics.Arcade.Image;
    const bottom = this.pipes.create(x, 0, "pipe") as Phaser.Physics.Arcade.Image;
    top.setFlipY(true);
    top.setDepth(2);
    bottom.setDepth(2);
    const pair = { top, bottom, scored: false };
    this.positionPipePair(pair, x);
    return pair;
  }

  private positionPipePair(pair: PipePair, x: number) {
    const gapCenter = Phaser.Math.Between(170, WORLD_HEIGHT - GROUND_HEIGHT - 160);
    const pipeTextureHeight = 520;
    const gap = this.currentPipeGap();
    pair.top.setPosition(x, gapCenter - gap / 2 - pipeTextureHeight / 2);
    pair.bottom.setPosition(x, gapCenter + gap / 2 + pipeTextureHeight / 2);
    this.refreshPipePair(pair);
  }

  private movePipes(delta: number) {
    const distance = (this.currentPipeSpeed() * delta) / 1000;
    for (const pair of this.pairs) {
      pair.top.x += distance;
      pair.bottom.x += distance;
      this.refreshPipePair(pair);
    }
  }

  private refreshPipePair(pair: PipePair) {
    pair.top.refreshBody();
    pair.bottom.refreshBody();
  }

  private recyclePipes() {
    let rightMost = Math.max(...this.pairs.map((pair) => pair.top.x));
    for (const pair of this.pairs) {
      if (pair.top.x < -PIPE_WIDTH) {
        rightMost += this.nextPipeDistance();
        this.positionPipePair(pair, rightMost);
        pair.scored = false;
      }
    }
  }

  private currentPipeSpeed() {
    const speed = PIPE_SPEED - this.model.score * 4;
    return Math.max(speed, FASTEST_PIPE_SPEED);
  }

  private currentPipeGap() {
    const gap = PIPE_GAP - Math.floor(this.model.score / 5) * 3;
    return Math.max(gap, MIN_PIPE_GAP);
  }

  private nextPipeDistance() {
    const distance = PIPE_DISTANCE + Phaser.Math.Between(-22, 32) - Math.floor(this.model.score / 10) * 3;
    return Phaser.Math.Clamp(distance, MIN_PIPE_DISTANCE, MAX_PIPE_DISTANCE);
  }

  private checkScoring() {
    for (const pair of this.pairs) {
      if (!pair.scored && pair.top.x + PIPE_WIDTH / 2 < this.bird.x) {
        pair.scored = true;
        this.model.scorePipe();
        this.publish();
        this.playTone(880, 0.055, "square");
      }
    }
  }

  private driftReadyBird(delta: number) {
    if (this.model.status !== "ready") {
      return;
    }

    const wave = Math.sin(this.time.now / 260) * 8;
    this.bird.setY(WORLD_HEIGHT / 2 + wave);
    this.bird.setAngle(Math.sin(this.time.now / 300) * 5);
    this.wrapClouds(delta);
  }

  private wrapClouds(delta: number) {
    this.clouds.children.each((child) => {
      const cloud = child as Phaser.GameObjects.Image;
      cloud.x -= delta * 0.012 * cloud.scaleX;
      if (cloud.x < -90) {
        cloud.x = WORLD_WIDTH + 90;
        cloud.y = Phaser.Math.Between(55, 365);
      }
      return true;
    });
  }

  private publish() {
    dispatchFlightState(this.model.snapshot());
  }

  private playTone(frequency: number, duration: number, type: OscillatorType) {
    if (this.muted) {
      return;
    }

    const AudioContextClass = window.AudioContext ?? window.webkitAudioContext;
    if (!AudioContextClass) {
      return;
    }

    const context = new AudioContextClass();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = type;
    oscillator.frequency.value = frequency;
    gain.gain.setValueAtTime(0.045, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + duration);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + duration);
    oscillator.addEventListener("ended", () => void context.close());
  }

  private createTextures() {
    const gfx = this.add.graphics();

    gfx.fillStyle(0x42272b);
    gfx.fillEllipse(28, 28, 42, 34);
    gfx.fillStyle(0xffd85c);
    gfx.fillEllipse(26, 26, 38, 30);
    gfx.fillStyle(0xfff3ac);
    gfx.fillEllipse(20, 30, 18, 16);
    gfx.fillStyle(0xffffff);
    gfx.fillCircle(35, 20, 6);
    gfx.fillStyle(0x151515);
    gfx.fillCircle(37, 20, 2);
    gfx.fillStyle(0xff8f43);
    gfx.fillTriangle(45, 25, 62, 31, 45, 36);
    gfx.generateTexture("bird-0", 70, 58);
    gfx.clear();

    gfx.fillStyle(0x42272b);
    gfx.fillEllipse(28, 28, 42, 34);
    gfx.fillStyle(0xffd85c);
    gfx.fillEllipse(26, 26, 38, 30);
    gfx.fillStyle(0xfff3ac);
    gfx.fillEllipse(18, 22, 18, 16);
    gfx.fillStyle(0xffffff);
    gfx.fillCircle(35, 20, 6);
    gfx.fillStyle(0x151515);
    gfx.fillCircle(37, 20, 2);
    gfx.fillStyle(0xff8f43);
    gfx.fillTriangle(45, 25, 62, 31, 45, 36);
    gfx.generateTexture("bird-1", 70, 58);
    gfx.clear();

    gfx.fillStyle(0x42272b);
    gfx.fillEllipse(28, 28, 42, 34);
    gfx.fillStyle(0xffd85c);
    gfx.fillEllipse(26, 26, 38, 30);
    gfx.fillStyle(0xfff3ac);
    gfx.fillEllipse(18, 36, 18, 16);
    gfx.fillStyle(0xffffff);
    gfx.fillCircle(35, 20, 6);
    gfx.fillStyle(0x151515);
    gfx.fillCircle(37, 22, 2);
    gfx.fillStyle(0xff8f43);
    gfx.fillTriangle(45, 25, 62, 31, 45, 36);
    gfx.generateTexture("bird-2", 70, 58);
    gfx.clear();

    gfx.fillStyle(0x24613c);
    gfx.fillRoundedRect(0, 0, PIPE_WIDTH, 520, 12);
    gfx.fillStyle(0x42b35d);
    gfx.fillRoundedRect(8, 0, PIPE_WIDTH - 16, 520, 8);
    gfx.fillStyle(0x7ff07f);
    gfx.fillRect(14, 0, 12, 520);
    gfx.fillStyle(0x1f5133);
    gfx.fillRoundedRect(-6, 0, PIPE_WIDTH + 12, 34, 10);
    gfx.generateTexture("pipe", PIPE_WIDTH + 12, 520);
    gfx.clear();

    gfx.fillStyle(0xffffff);
    gfx.fillEllipse(42, 30, 80, 34);
    gfx.fillEllipse(78, 26, 66, 42);
    gfx.fillEllipse(110, 32, 76, 30);
    gfx.generateTexture("cloud", 152, 64);
    gfx.clear();

    gfx.fillStyle(0x8fd653);
    gfx.fillRect(0, 0, WORLD_WIDTH, GROUND_HEIGHT);
    gfx.generateTexture("ground", WORLD_WIDTH, GROUND_HEIGHT);
    gfx.destroy();
  }
}
